import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Briefcase, Check, CircleCheck, Clock, Gauge, Layers, ListChecks, Loader2, Play, Settings2,
  NotebookPen, Shuffle, Target, Timer, TrendingDown, Zap,
} from 'lucide-react';
import { apiErrorMessage, errorService, examService, questionService, topicService, courseService } from '../services/api';
import { CourseContext } from '../contexts/CourseContext';
import { getPhaseColor } from '../theme/phases';
import { AppShell, Breadcrumbs, Button, Card, Field, Loader, PageHeader } from '../components/ui';
import './ExamConfig.css';

const DEFAULT_FASES = [1, 2, 3, 4, 5].map(num => ({ num, name: `Fase ${num}`, color: getPhaseColor(num) }));

// O valor 'fiap' é o que a API espera para o modo com 80% de casos práticos.
const EXAM_STYLES = [
  { value: 'fiap', label: 'Estudo de caso', titlePrefix: 'Estudo de caso', icon: Briefcase },
  { value: 'review', label: 'Revisão rápida', titlePrefix: 'Revisão rápida', icon: Zap },
  { value: 'mixed', label: 'Misto', titlePrefix: 'Simulado', icon: Shuffle },
  // Gerados a partir do seu histórico
  { value: 'errors', label: 'Meus erros', titlePrefix: 'Meus erros', icon: NotebookPen },
  { value: 'weak', label: 'Tópicos fracos', titlePrefix: 'Tópicos fracos', icon: TrendingDown },
];

const PRESETS = [
  { value: 'quick', label: 'Rápido', questions: 30, minutes: 60, icon: Timer },
  { value: 'full', label: 'Completo', questions: 80, minutes: 180, icon: Gauge },
  { value: 'custom', label: 'Personalizado', icon: Settings2 },
];

const CORRECTION_MODES = [
  { value: 'end', label: 'Ao final', description: 'Veja o gabarito só quando terminar, como numa prova real', icon: Clock },
  { value: 'immediate', label: 'A cada questão', description: 'Receba a correção e a explicação na hora', icon: CircleCheck },
];

function ExamConfig() {
  const { courseSlug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentCourse, selectCourse, getPhases } = useContext(CourseContext);
  const [config, setConfig] = useState({
    title: 'Simulado',
    examMode: 'simulation',
    correctionMode: 'end',
    totalQuestions: 30,
    durationMinutes: 60,
    categoryIds: [], // IDs das categorias (tópicos) selecionadas
    examStyle: 'mixed', // 'fiap' (80% case), 'review' (só direct), 'mixed' (todas)
  });
  const [preset, setPreset] = useState('quick');
  const [creating, setCreating] = useState(false);
  const [totalAvailable, setTotalAvailable] = useState(0);
  const [styleCounts, setStyleCounts] = useState({ direct: 0, case: 0, total: 0 });
  const [categoryStyleCounts, setCategoryStyleCounts] = useState({}); // { categoryId: { direct, case, total } }
  const [loading, setLoading] = useState(true);
  const [topics, setTopics] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedFases, setSelectedFases] = useState(new Set());
  const [selectedTopics, setSelectedTopics] = useState(new Set());
  const [fases, setFases] = useState(DEFAULT_FASES);
  const [submitError, setSubmitError] = useState(null);
  const [errorCount, setErrorCount] = useState(0);

  // Atalhos vindos de outras telas: /config?style=errors abre já em "Meus erros"
  useEffect(() => {
    const style = searchParams.get('style');
    if (EXAM_STYLES.some(s => s.value === style)) {
      setConfig(prev => ({ ...prev, examStyle: style }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (courseSlug) errorService.countErrors(null, courseSlug).then(setErrorCount).catch(() => {});
  }, [courseSlug]);

  // Select course when component mounts
  useEffect(() => {
    if (courseSlug) {
      selectCourse(courseSlug);
    }
  }, [courseSlug, selectCourse]);

  // Update fases when course changes
  useEffect(() => {
    if (currentCourse) {
      const coursePhases = getPhases();
      if (coursePhases && coursePhases.length > 0) {
        setFases(coursePhases);
      }
      // Update title with course name
      setConfig(prev => ({
        ...prev,
        title: `Simulado · ${currentCourse.name}`,
      }));
    }
  }, [currentCourse, getPhases]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Use course-filtered topics if courseSlug provided
        const topicsPromise = courseSlug
          ? courseService.getTopicsByCourse(courseSlug)
          : topicService.getAllTopics();

        const [counts, topicsData, categoriesData, catStyleCounts] = await Promise.all([
          questionService.countByStyle(courseSlug),
          topicsPromise,
          questionService.getCategories(courseSlug),
          questionService.countByCategoryAndStyle(courseSlug),
        ]);
        setStyleCounts(counts);
        setTotalAvailable(counts.total);
        setTopics(topicsData);
        setCategories(categoriesData);

        // Mapear contagens por categoria
        const catStyleMap = {};
        catStyleCounts.forEach(item => {
          catStyleMap[item.categoryName] = {
            direct: item.direct || 0,
            case: item.case || 0,
            total: item.total || 0,
          };
        });
        setCategoryStyleCounts(catStyleMap);

        setConfig(prev => ({ ...prev, totalQuestions: Math.min(prev.totalQuestions, counts.total) }));
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [courseSlug]);

  // Agrupa tópicos por fase
  const topicsByFase = fases.map(fase => ({
    ...fase,
    topics: topics.filter(t => t.fase === fase.num),
    categories: categories.filter(c => {
      const topic = topics.find(t => t.name === c.name);
      return topic && topic.fase === fase.num;
    }),
  }));

  // Calcula questões disponíveis baseado no estilo selecionado
  const getAvailableByStyle = () => {
    switch (config.examStyle) {
      case 'fiap':
        // 80% case + 20% direct, limitado pelo disponível
        return Math.min(styleCounts.total, styleCounts.case + Math.floor(styleCounts.case * 0.25));
      case 'review':
        return styleCounts.direct;
      default:
        return styleCounts.total;
    }
  };

  // Retorna contagem de questões de uma categoria baseada no estilo
  const getCategoryCountByStyle = (categoryName) => {
    const catCounts = categoryStyleCounts[categoryName];
    if (!catCounts) return 0;

    switch (config.examStyle) {
      case 'fiap':
        // No estudo de caso, mostra as questões de caso + a parte das diretas que seria usada
        return catCounts.case + Math.min(catCounts.direct, Math.floor(catCounts.case * 0.25));
      case 'review':
        return catCounts.direct;
      default:
        return catCounts.total;
    }
  };

  // Calcula questões disponíveis com filtros de tópico
  const getFilteredQuestionCount = () => {
    if (config.examStyle === 'errors') return errorCount;
    // Os tópicos fracos são escolhidos pelo servidor a partir do seu desempenho
    if (config.examStyle === 'weak') return styleCounts.total;
    if (selectedTopics.size === 0) return getAvailableByStyle();

    // Soma as questões dos tópicos selecionados baseado no estilo
    return Array.from(selectedTopics).reduce((acc, topicName) => {
      return acc + getCategoryCountByStyle(topicName);
    }, 0);
  };

  const handleFaseToggle = (faseNum) => {
    const newSelectedFases = new Set(selectedFases);
    const faseTopicNames = topicsByFase
      .find(f => f.num === faseNum)?.categories
      .map(c => c.name) || [];

    if (newSelectedFases.has(faseNum)) {
      newSelectedFases.delete(faseNum);
      // Remove todos os tópicos dessa fase
      const newSelectedTopics = new Set(selectedTopics);
      faseTopicNames.forEach(name => newSelectedTopics.delete(name));
      setSelectedTopics(newSelectedTopics);
    } else {
      newSelectedFases.add(faseNum);
      // Adiciona todos os tópicos dessa fase
      const newSelectedTopics = new Set(selectedTopics);
      faseTopicNames.forEach(name => newSelectedTopics.add(name));
      setSelectedTopics(newSelectedTopics);
    }
    setSelectedFases(newSelectedFases);
  };

  const handleTopicToggle = (topicName, faseNum) => {
    const newSelectedTopics = new Set(selectedTopics);
    if (newSelectedTopics.has(topicName)) {
      newSelectedTopics.delete(topicName);
    } else {
      newSelectedTopics.add(topicName);
    }
    setSelectedTopics(newSelectedTopics);

    // Atualiza estado da fase
    const faseTopicNames = topicsByFase
      .find(f => f.num === faseNum)?.categories
      .map(c => c.name) || [];
    const allSelected = faseTopicNames.every(name => newSelectedTopics.has(name));
    const newSelectedFases = new Set(selectedFases);
    if (allSelected && faseTopicNames.length > 0) {
      newSelectedFases.add(faseNum);
    } else {
      newSelectedFases.delete(faseNum);
    }
    setSelectedFases(newSelectedFases);
  };

  const handleSelectAll = () => {
    const allTopicNames = categories.map(c => c.name);
    setSelectedTopics(new Set(allTopicNames));
    setSelectedFases(new Set(fases.map(f => f.num)));
  };

  const handleClearAll = () => {
    setSelectedTopics(new Set());
    setSelectedFases(new Set());
  };

  // Helper function to build paths with courseSlug
  const buildPath = (path) => {
    return courseSlug ? `/curso/${courseSlug}${path}` : path;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreating(true);
    setSubmitError(null);

    try {
      const categoryIds = categories
        .filter(c => selectedTopics.has(c.name))
        .map(c => c.id);

      const examConfig = {
        title: config.title,
        correctionMode: config.correctionMode,
        totalQuestions: config.totalQuestions,
        durationMinutes: config.durationMinutes,
        categoryIds: categoryIds.length > 0 ? categoryIds : null,
        examStyle: config.examStyle,
        courseSlug: courseSlug || null,
      };

      const exam = await examService.createExam(examConfig);
      navigate(buildPath(`/exam/${exam.id}`));
    } catch (error) {
      console.error('Error creating exam:', error);
      setSubmitError(apiErrorMessage(error, 'Não foi possível criar o simulado. Tente novamente.'));
      setCreating(false);
    }
  };

  const filteredCount = getFilteredQuestionCount();
  const questionCount = Math.min(config.totalQuestions, filteredCount);
  const courseName = currentCourse?.name;
  const styleLabel = EXAM_STYLES.find(s => s.value === config.examStyle)?.label;

  const selectStyle = (style) => {
    setConfig({ ...config, examStyle: style.value, title: `${style.titlePrefix}${courseName ? ` · ${courseName}` : ''}` });
  };

  const selectPreset = (p) => {
    setPreset(p.value);
    if (p.questions) {
      setConfig({ ...config, totalQuestions: Math.min(p.questions, filteredCount), durationMinutes: p.minutes });
    }
  };

  const styleDesc = {
    fiap: `80% casos práticos · ${styleCounts.case} questões de caso`,
    review: `Só questões diretas · ${styleCounts.direct} disponíveis`,
    mixed: `Todos os tipos · ${styleCounts.total} questões`,
    errors: errorCount > 0 ? `${errorCount} ${errorCount === 1 ? 'questão' : 'questões'} que você errou` : 'Nenhum erro pendente',
    weak: 'Foca nos tópicos em que você mais erra',
  };

  if (loading) {
    return (
      <AppShell section="Novo simulado" courseName={courseName}>
        <Loader label="Carregando questões..." />
      </AppShell>
    );
  }

  return (
    <AppShell section="Novo simulado" courseName={courseName}>
      <Breadcrumbs items={[{ label: 'Modo Estudo', onClick: () => navigate(buildPath('/estudo')) }, { label: 'Novo simulado' }]} />
      <PageHeader
        icon={Target}
        eyebrow={courseName}
        title="Novo simulado"
        subtitle="Escolha o tipo de questão, o tamanho e os tópicos. O resumo ao lado mostra como vai ficar."
      />

      <form onSubmit={handleSubmit} className="ec">
        <div className="ec__steps">
          <Step num={1} title="Tipo de questão">
            <div className="ec-choices">
              {EXAM_STYLES.map(style => (
                <Choice
                  key={style.value}
                  icon={style.icon}
                  title={style.label}
                  description={styleDesc[style.value]}
                  active={config.examStyle === style.value}
                  onClick={() => selectStyle(style)}
                />
              ))}
            </div>
          </Step>

          <Step num={2} title="Tamanho">
            <div className="ec-choices">
              {PRESETS.map(p => (
                <Choice
                  key={p.value}
                  icon={p.icon}
                  title={p.label}
                  description={p.questions ? `${p.questions} questões · ${p.minutes} min` : 'Você define questões e tempo'}
                  active={preset === p.value}
                  onClick={() => selectPreset(p)}
                />
              ))}
            </div>
            {preset === 'custom' && (
              <div className="ec-custom">
                <Field label="Número de questões" hint={`Disponíveis com os filtros atuais: ${filteredCount}`}>
                  <input
                    type="number"
                    min="1"
                    max={filteredCount}
                    value={questionCount}
                    onChange={(e) => setConfig({ ...config, totalQuestions: parseInt(e.target.value) })}
                  />
                </Field>
                <Field label="Duração (minutos)" hint="Entre 10 e 300 minutos">
                  <input
                    type="number"
                    min="10"
                    max="300"
                    value={config.durationMinutes}
                    onChange={(e) => setConfig({ ...config, durationMinutes: parseInt(e.target.value) })}
                  />
                </Field>
              </div>
            )}
          </Step>

          {config.examStyle !== 'weak' && (
          <Step
            num={3}
            title="Tópicos"
            aside={
              <div className="ec-filter-actions">
                <Button size="sm" variant="ghost" onClick={handleSelectAll}>Selecionar todos</Button>
                <Button size="sm" variant="ghost" onClick={handleClearAll} disabled={selectedTopics.size === 0}>Limpar</Button>
              </div>
            }
          >
            <p className="ec-hint">
              {selectedTopics.size === 0
                ? `Sem filtro, o simulado sorteia entre todas as ${totalAvailable} questões do curso.`
                : `${selectedTopics.size} ${selectedTopics.size === 1 ? 'tópico selecionado' : 'tópicos selecionados'} · ${filteredCount} questões disponíveis.`}
            </p>
            <div className="ec-phases">
              {topicsByFase.filter(fase => fase.categories.length > 0).map(fase => {
                const faseOn = selectedFases.has(fase.num);
                return (
                  <div key={fase.num} className="ec-phase">
                    <button
                      type="button"
                      className={`ec-phase__head ${faseOn ? 'is-on' : ''}`}
                      onClick={() => handleFaseToggle(fase.num)}
                      aria-pressed={faseOn}
                    >
                      <span className="ec-check" aria-hidden="true">{faseOn && <Check size={14} strokeWidth={3} />}</span>
                      <span className="ec-phase__num" style={{ background: fase.color }}>{String(fase.num).padStart(2, '0')}</span>
                      <span className="ec-phase__name">{fase.name}</span>
                    </button>
                    <div className="ec-topics">
                      {fase.categories.map(cat => {
                        const count = getCategoryCountByStyle(cat.name);
                        const on = selectedTopics.has(cat.name);
                        return (
                          <button
                            key={cat.id}
                            type="button"
                            className={`ec-topic ${on ? 'is-on' : ''}`}
                            onClick={() => handleTopicToggle(cat.name, fase.num)}
                            disabled={count === 0}
                            aria-pressed={on}
                          >
                            <span className="ec-check" aria-hidden="true">{on && <Check size={12} strokeWidth={3} />}</span>
                            <span className="ec-topic__name">{cat.name}</span>
                            <span className="ec-topic__count">{count}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </Step>

          )}

          <Step num={config.examStyle === 'weak' ? 3 : 4} title="Correção">
            <div className="ec-choices ec-choices--two">
              {CORRECTION_MODES.map(m => (
                <Choice
                  key={m.value}
                  icon={m.icon}
                  title={m.label}
                  description={m.description}
                  active={config.correctionMode === m.value}
                  onClick={() => setConfig({ ...config, correctionMode: m.value })}
                />
              ))}
            </div>
          </Step>
        </div>

        <aside className="ec__summary">
          <div className="ec-summary">
            <span className="ec-summary__label">Resumo do simulado</span>
            <div className="ec-summary__big">
              <span className="ec-summary__num">{questionCount}</span>
              <span className="ec-summary__unit">questões</span>
            </div>
            <ul className="ec-summary__list">
              <li><Clock size={16} aria-hidden="true" /> {config.durationMinutes} minutos</li>
              <li><Layers size={16} aria-hidden="true" /> {styleLabel}</li>
              <li>
                <ListChecks size={16} aria-hidden="true" />
                {selectedTopics.size === 0 ? 'Todos os tópicos' : `${selectedTopics.size} ${selectedTopics.size === 1 ? 'tópico' : 'tópicos'}`}
              </li>
              <li>
                <CircleCheck size={16} aria-hidden="true" />
                {config.correctionMode === 'immediate' ? 'Correção a cada questão' : 'Correção ao final'}
              </li>
            </ul>
            {filteredCount === 0 && (
              <p className="ec-summary__warn">Nenhuma questão disponível com esses filtros.</p>
            )}
            {submitError && <p className="ec-summary__warn">{submitError}</p>}
            <Button
              type="submit"
              variant="accent"
              size="lg"
              block
              icon={creating ? Loader2 : Play}
              disabled={creating || filteredCount === 0}
            >
              {creating ? 'Criando...' : 'Iniciar simulado'}
            </Button>
            <Button variant="ghost" block className="ec-summary__back" icon={ArrowLeft} onClick={() => navigate(buildPath('/estudo'))}>
              Voltar ao estudo
            </Button>
          </div>
        </aside>
      </form>
    </AppShell>
  );
}

function Step({ num, title, aside, children }) {
  return (
    <Card padding="lg" className="ec-step">
      <div className="ec-step__head">
        <span className="ec-step__num">{String(num).padStart(2, '0')}</span>
        <h2>{title}</h2>
        {aside && <div className="ec-step__aside">{aside}</div>}
      </div>
      {children}
    </Card>
  );
}

function Choice({ icon: Icon, title, description, active, onClick }) {
  return (
    <button type="button" className={`ec-choice ${active ? 'is-on' : ''}`} onClick={onClick} aria-pressed={active}>
      <span className="ec-choice__icon"><Icon size={20} aria-hidden="true" /></span>
      <span className="ec-choice__text">
        <span className="ec-choice__title">{title}</span>
        <span className="ec-choice__desc">{description}</span>
      </span>
      <span className="ec-choice__radio" aria-hidden="true">{active && <Check size={13} strokeWidth={3} />}</span>
    </button>
  );
}

export default ExamConfig;
